// Copyright (c) StrangeBee 2024

import React, { ReactElement } from 'react';
import THDrawer from '@Molecules/THDrawer/THDrawer';
import { StorageView } from '@Hooks/useStorageView';

import { ColumnTitle } from '@Atoms/ColumnTitle/ColumnTitle';
import THTable from '@Molecules/THTable/THTable';
import { ActionsList } from '@Atoms/ActionsList/ActionsList';
import { useLocalMessage } from '@Helpers/locale';

import './ManageViewListDrawer.scss';
import { ICONS } from '@Constants/iconsApp';

interface ManageViewListDrawerProps {
    views: StorageView;
    onDelete?: (viewId: string) => void;
    triggerElem?: ReactElement;
    visible?: boolean;
    onVisibleChange?: (visible: boolean) => void;
}

export const ManageViewListDrawer = ({
    views,
    triggerElem,
    onDelete,
    visible,
    onVisibleChange,
}: ManageViewListDrawerProps): ReactElement => {
    const i = useLocalMessage();

    return (
        <THDrawer
            title={i('view.drawer.title.list')}
            triggerElem={triggerElem}
            visible={visible}
            afterVisibleChange={(visible) => {
                onVisibleChange?.(visible);
            }}
            className="drawer-form"
            width="50vw"
        >
            <div className="manage-view-drawer__content">
                <THTable
                    dataSource={Object.keys(views)}
                    columns={[
                        {
                            title: <ColumnTitle>{i('view.fields.name')}</ColumnTitle>,
                            className: 'manage-view-drawer-list__column__name custom-table__cell',
                            render: (_, name): ReactElement => <span>{name}</span>,
                        },
                        {
                            title: <ColumnTitle>{i('view.fields.actions')}</ColumnTitle>,
                            className: 'manage-view-drawer-list__column__action custom-table__cell',
                            render: (_, name): ReactElement | null => (
                                <ActionsList
                                    actionList={{
                                        delete: {
                                            label: i('general.delete'),
                                            confirm: true,
                                            message: i('view.message.confirm-delete'),
                                            query: () => onDelete?.(name),
                                            icon: <ICONS.DeleteIcon />,
                                            disabled: name === 'default' || !onDelete,
                                        },
                                    }}
                                    isIcon
                                    scope="single"
                                />
                            ),
                        },
                    ]}
                    loading={false}
                />
            </div>
        </THDrawer>
    );
};
